<template>
  <div id="player" :class="{ 'has-menu-open': showMenu }">
  <div class="player-wrapper">
  <div class="o-grid o-grid_full_h">
    <div class="o-grid__row">
      <div class="o-grid__cell o-grid__row_compact">
        <header class="player-header">
          <div class="player-header__sideitem u-tal">
            <button v-if="canCloseApp" v-on:click="closeApp" :title="$t('close_app')">
              <icon name="close" scale="1"></icon>&nbsp;
            </button>
          </div>

          <remaining-time-for-loan :loanEndDate="loanEndDate"></remaining-time-for-loan>

          <div class="player-header__sideitem u-tar">
            <player-menu-toggler v-if="menuHasItems" v-on:toggleplayermenu="toggleMenu"></player-menu-toggler>
          </div>
        </header>
      </div>
    </div>

    <div class="o-grid__row">
      <div class="o-grid__cell cover">
        <cover class="cover__img" :src="coverUrl" v-on:themecolorextracted="emitThemeColor"></cover>
      </div>
    </div>

    <div class="o-grid__row">
      <div class="o-grid__cell o-grid__row_compact">
        <div class="o-wrapper">
        <header class="header">
          <h1 v-if="bookTitle" class="header__title">{{ bookTitle }}</h1>

          <div class="header__subtitle">
            <table-of-contents :chapters="this.book.spine" :selected="currentlyPlaying" />
          </div>
        </header>

        <div class="progress-group">
          <div class="progress-group__pbar">
            <progress-bar
              v-on:timeupdate="updatePlaybackProgress"
              :currentTime="currentTime"
              :initialTime="initialTime"
              :duration="trackDuration"
              :themeColor="themeColor"
              :isMenuOpen="isMenuOpen"></progress-bar>
          </div>
          <div v-if="volumeUpdateSupported" class="progress-group__vol">
            <volume
              v-on:volumeupdate="updateVolume"
              :volume="volume"
              :themeColor="themeColor"
              :isMenuOpen="isMenuOpen"></volume>
          </div>
        </div>
        </div>
      </div>
    </div>

    <div class="o-grid__row">
      <div class="o-grid__cell o-grid__row_compact">
        <div class="controls-group">
          <audio-controls ref="audio" :sources="audioSources"
            v-on:timeupdate="updatePlaybackProgress"
            v-on:bookended="showBookEndedModal = true"
            :trackEnded="nextTrack"
            :playing="playing"
            :currentTime="currentTime"
            :initialTime="initialTime"
            :volume="volume"
            :playbackRate="playbackRate"
            :bookTitle="bookTitle"
            :isLastTrack="isLastTrack"></audio-controls>

          <div class="speedo">
            <playback-rate :rate="playbackRate" v-on:rateupdate="updatePlaybackRate"></playback-rate>
          </div>
        </div>
      </div>
    </div>
  </div>
  </div>

  <modal v-if="loanEndDate" :isOpen="showBookEndedModal" v-on:closemodal="closeBookEndedModal">
    <ending-modal-content :returnUrl="returnUrl" v-on:close="closeBookEndedModal"></ending-modal-content>
  </modal>

  <player-menu v-if="menuHasItems">
    <template slot="return-book-button">
      <button v-on:click="showReturnBookConfirmModal = true" class="player-menu__item">{{ $t('return_book') }}</button>
      <modal :isOpen="showReturnBookConfirmModal" v-on:closemodal="showReturnBookConfirmModal = false">
        <p class="u-font-lg">{{ $t('return_book_confirm') }}</p>
        <p>
          <return-book-button :buttonText="$t('yes')" :returnUrl="returnUrl" class="button"></return-book-button>&nbsp;
          <button v-on:click="showReturnBookConfirmModal = false" class="button">{{ $t('no') }}</button>
        </p>
      </modal>
    </template>
  </player-menu>
  </div>
</template>

<script>
import PlayerMenuToggler from './components/PlayerMenuToggler'
import PlayerMenu from './components/PlayerMenu'
import ReturnBookButton from './components/ReturnBookButton'
import AudioControls from './components/AudioControls'
import Cover from './components/Cover'
import ProgressBar from './components/ProgressBar'
import PlaybackRate from './components/PlaybackRate'
import Volume from './components/Volume'
import TableOfContents from './components/TableOfContents'
import RemainingTimeForLoan from './components/RemainingTimeForLoan'
import EndingModalContent from './components/EndingModalContent'
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {
    PlayerMenuToggler,
    PlayerMenu,
    ReturnBookButton,
    AudioControls,
    Cover,
    ProgressBar,
    PlaybackRate,
    Volume,
    TableOfContents,
    RemainingTimeForLoan,
    EndingModalContent
  },

  props: {
    themeColor: {
      type: Array,
      default: [0, 0, 0]
    }
  },

  data () {
    return {
      firstStarted: false,
      showMenu: false,
      // isMenuOpen swith to the same value of showMenu after a delay
      // of 300ms (css animation time).
      isMenuOpen: false,
      showBookEndedModal: false,
      showReturnBookConfirmModal: false
    }
  },

  computed: {
    bookTitle () {
      return this.book.metadata.title
    },

    coverUrl () {
      let coverLink = this.book.links.find(res => res.rel === 'cover')
      if (!coverLink) return
      return coverLink.href
    },

    returnUrl () {
      let returnLink = this.book.links.find(res => res.rel === 'return')
      if (!returnLink) return
      return returnLink.href
    },

    loanEndDate (state) {
      let rights = this.book.metadata['http://www.feedbooks.com/audiobooks/rights']
      if (!rights) return
      return rights.end
    },

    trackDuration () {
      return this.currentlyPlaying.duration
    },

    audioSources () {
      let sources = [this.currentlyPlaying]
      if (this.currentlyPlaying.alternates) {
        sources = this.currentlyPlaying.alternates.concat(sources)
      }
      return sources
    },

    menuHasItems () {
      return !!this.returnUrl
    },

    isLastTrack () {
      return this.currentlyPlaying === [...this.book.spine].pop()
    },

    volumeUpdateSupported () {
      let audio = new Audio()

      // Set volume to 0 to check if volume is updatable.
      audio.volume = 0

      // Detect if the device is mobile.
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

      // The volume must be updatable and the device must not be mobile.
      return audio.volume == 0 && !isMobile
    },

    ...mapGetters({
      'book': 'book',
      'currentlyPlaying': 'currentItem',
      'currentTime': 'currentTime',
      'initialTime': 'initialTime',
      'playing': 'playing',
      'progress': 'progress',
      'playbackRate': 'playbackRate',
      'volume': 'volume',
      'overallProgress': 'overallProgress'
    })
  },

  watch: {
    showMenu () {
      // Change the state of isMenuOpen when css animation is done
      setTimeout(() => { this.isMenuOpen = !this.isMenuOpen }, 300)
    }
  },

  methods: {
    emitThemeColor (color) {
      this.$emit('themecolorextracted', color)
    },

    toggleMenu (value) {
      this.showMenu = value
    },

    closeBookEndedModal () {
      this.showBookEndedModal = false
      // Fix a small 2 issues:
      // - Track restarting to the beggining when pressing play
      // - If the user refresh the page after BookendedModal been closed it will reappear
      this.updatePlaybackProgress(this.currentTime - 0.1)
    },

    ...mapActions(['nextTrack', 'updatePlaybackProgress', 'updatePlaybackRate', 'updateVolume'])
  },

  mounted () {
    this.$store.dispatch('fetchSetting')
    document.title = this.bookTitle || this.$i18n.t('audiobook')
  }
}
</script>

<style>
[id="player"] {
  min-height: 100%;
  display: flex;
  overflow: hidden;
}

.player-header {
  padding: 1em;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.player-header__sideitem {
  width: 6em;
}

.player-header__info {
  text-align: center;
  font-size: 0.87em;
}

.player-header__info > * {
  display: inline-block;
}

.player-wrapper {
  width: 100%;
  transition: margin-left 300ms;
}

.player-menu {
  width: 80%;
  margin-right: -80%;
  transition: margin-right 300ms;
}

[id="player"].has-menu-open .player-wrapper {
  margin-left: -80%;
}

[id="player"].has-menu-open .player-menu {
  margin-right: 0;
}

@media screen and (min-width: 600px) {
  .player-menu {
    width: 33%;
    margin-right: -33%;
  }

  [id="player"].has-menu-open .player-wrapper {
    margin-left: 0;
  }
}

.header {
  text-align: center;
}

.header__title {
  margin-top: 0;
  margin-bottom: 0.25em;
  font-size: 1em;
  line-height: 1;
}

.header__subtitle {
  margin-top: 0;
  margin-bottom: 0;
}

.cover {
  position: relative;
}

.cover__img {
  position: absolute;
  top: 0;
  bottom: 1.5em;
  right: 2em;
  left: 2em;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  /* Create to much lag on Firefox */
  /* filter: drop-shadow(0 1em 3em rgba(0,0,0, 0.6)); */
}

@media screen and (min-width: 600px) and (min-height: 800px) {
  .cover__img {
    background-size: 460px auto;
  }
}

.progress-group {
  padding-top: 2em;
  padding-bottom: 1.75em;
  display: flex;
  align-items: center;
}

.progress-group__pbar {
  flex-grow: 1;
}

.progress-group__vol {
  padding-left: 1.25em;
}

.controls-group {
  padding-bottom: 2em;
}

/* Double the class selector to override the default component CSS */
.vue-slider-component .vue-slider-dot.vue-slider-dot {
  background-color: transparent;
  box-shadow: none;
  padding: 8px;
}

@media screen and (min-width: 600px) {
  .player-header__info {
    font-size: 1em;
  }

  .header__title {
    font-size: 1.25em;
  }

  .progress-group__vol {
    padding-left: 2em;
    width: 150px;
  }
}

.speedo {
  margin-top: 1.25em;
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/Player.vue